import { ComponentsLocaleProvider } from "@aurem/components";
import {
  allTranslations,
  getCurrentLanguage,
  getTranslations,
  languageKeys,
  translationKey,
  traversePath,
  useI18n as useI18nBase,
} from "@aurem/translations";
import { LocaleProvider } from "@aurem/ui";
import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";
import type { I18nContextType, TranslationKey } from "./types";
import { useTranslations } from "./useTranslations";

export type {
  Direction,
  FlattenedTranslations,
  I18nContextType,
  Language,
  TranslationKey,
  TranslationParams,
  Translations,
} from "./types";

export {
  allTranslations,
  getCurrentLanguage,
  getTranslations,
  languageKeys,
  translationKey,
  traversePath,
};

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const baseI18n = useI18nBase();
  const { language } = baseI18n;
  const { t, tRich } = useTranslations(language);

  const contextValue = useMemo(
    (): I18nContextType => ({
      ...baseI18n,
      t,
      tRich,
    }),
    [baseI18n, t, tRich],
  );

  return (
    <I18nContext.Provider value={contextValue}>
      <LocaleProvider language={language}>
        <ComponentsLocaleProvider
          language={language}
          t={t as (key: string, ...params: unknown[]) => string}
        >
          {children}
        </ComponentsLocaleProvider>
      </LocaleProvider>
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
};

export const tPath = <T extends TranslationKey>(path: T): T => path;
